import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Users,
  Calendar,
  Stethoscope,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Clock,
  BarChart3,
  Building2,
  FileText,
  Sparkles,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const features = [
  {
    icon: Users,
    title: 'OPD & Patient Management',
    description: 'Comprehensive digital health records with Aadhaar/ABHA identifiers, allergies, chronic conditions, and past consultation history.',
  },
  {
    icon: Calendar,
    title: 'Smart OPD Slot Scheduling',
    description: 'Effortless outpatient token booking with doctor availability, multi-clinic schedules, and conflict-free calendar views.',
  },
  {
    icon: Stethoscope,
    title: '2-Pane Clinical Workspace',
    description: 'Physician-friendly interface for vitals with auto-calculated BMI, chief complaints, diagnosis, and Indian pharma drug prescriptions.',
  },
  {
    icon: Building2,
    title: 'Multi-Clinic Architecture',
    description: 'Seamlessly switch between General OPD, Cardiology, Pediatrics, and specialty departments under a single hospital umbrella.',
  },
  {
    icon: BarChart3,
    title: 'Clinical Operations Analytics',
    description: 'Real-time metrics on daily OPD visits, consultation turnaround times, doctor productivity, and INR revenue tracking.',
  },
  {
    icon: ShieldCheck,
    title: 'NABH & ABDM Ready',
    description: 'Standardized clinical documentation and role-based access control (Admin, Doctor, Receptionist) built for Indian healthcare guidelines.',
  },
];

const stats = [
  { value: '50,000+', label: 'Outpatients Managed' },
  { value: '100+', label: 'Specialist Doctors' },
  { value: '15 Mins', label: 'Avg. OPD Turnaround' },
  { value: '99.9%', label: 'Clinical Uptime' },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="landing-page">
      {/* Top Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shadow-sm">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-heading font-bold text-xl text-slate-900 tracking-tight">PulseFlow</span>
                <span className="text-[10px] uppercase font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded ml-2 border border-teal-200">
                  PMS India
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-slate-700 font-semibold hover:text-teal-700"
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-teal-600 hover:bg-teal-700 font-bold text-white shadow-sm"
                data-testid="nav-get-started-btn"
              >
                Launch PMS
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Copy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                Modern Healthcare OPD & Clinical Operations System
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                Streamline Your <br />
                <span className="text-teal-600 bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                  Hospital & Clinic OPDs
                </span>
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                PulseFlow is a high-performance Patient Management System designed for Indian hospitals, polyclinics, and nursing homes. Manage daily walk-ins, OPD appointments, electronic prescriptions, and multi-department triage with precision.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/login')}
                  className="bg-teal-600 hover:bg-teal-700 h-12 px-7 text-sm font-bold text-white shadow-md"
                  data-testid="hero-get-started-btn"
                >
                  Open Hospital Portal
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="h-12 px-7 text-sm font-semibold border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="hero-demo-btn"
                >
                  Explore Doctor & OPD Demo
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-4 text-xs font-semibold text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-600" />
                  Multi-Clinic Switching
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-600" />
                  Indian Drug Formulary Autocomplete
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-600" />
                  Zero Latency 2-Pane Doctor EMR
                </div>
              </div>
            </div>

            {/* Right Architectural Hospital Visual (No People) */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-3xl blur-2xl opacity-20" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-900">
                <img 
                  src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80"
                  alt="Modern Hospital Architecture & Clean Clinical Facility"
                  className="w-full h-80 object-cover opacity-90 hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent p-6 flex flex-col justify-end text-white">
                  <Badge className="w-fit bg-teal-500 text-slate-950 font-bold text-[10px] mb-2 uppercase">
                    Pristine Clinical Environment
                  </Badge>
                  <p className="font-bold text-sm text-white">Advanced Outpatient Department Architecture</p>
                  <p className="text-xs text-slate-300">Designed for rapid consultation triage and seamless patient care.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="py-12 bg-slate-900 text-white border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center space-y-1">
                <p className="text-3xl sm:text-4xl font-extrabold text-teal-400 font-mono">{stat.value}</p>
                <p className="text-xs sm:text-sm font-medium text-slate-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h2 className="text-3xl font-heading font-extrabold text-slate-900 tracking-tight">
              Engineered for Real Hospital Workflows
            </h2>
            <p className="text-sm text-slate-500">
              From front-desk token registration to physician electronic medical records and management analytics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:border-teal-300 hover:shadow-md transition-all">
                <CardContent className="p-6 space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-base text-slate-900">{feature.title}</h3>
                  <p className="text-xs leading-relaxed text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-900 to-slate-900 text-white text-center px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl font-bold font-heading">Ready to modernize your hospital's patient care?</h2>
          <p className="text-slate-300 text-sm">
            Experience PulseFlow's lightning-fast consultation workspace and clinical triage queue today.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/login')}
            className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold px-8 h-12 text-sm shadow-lg"
          >
            Access Healthcare Portal Now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-slate-400 text-xs text-center border-t border-slate-800">
        <p>© 2026 PulseFlow Healthcare Suite • Patient Management System (PMS)</p>
      </footer>
    </div>
  );
};

export default LandingPage;
